import { InfoWindow } from "@react-google-maps/api";
import { useState, useEffect } from "react";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PhoneIcon from '@mui/icons-material/Phone';
import { useDispatch, useSelector } from 'react-redux';
import { getTripApi } from "services/TripService";

export default function InfoWindowComponent({ driver, onClose, position, loading, updatedSelectedDriver }) {
  const dispatch = useDispatch();
  const [copiedField, setCopiedField] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [tripDetails, setTripDetails] = useState(null);

  const handleCopy = (value, field) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleViewDetails = async () => {
    if(updatedSelectedDriver?.status !== 'onTrip') {
      console.warn('Driver is not currently on a trip.');
      return;
    }

    setIsLoadingDetails(true);
    
    try {
      const tripData = await getTripApi(driver.tripId);
      if (tripData.success) {
        setTripDetails(tripData.data);
      } else {
        console.error('Failed to fetch trip details:', tripData.message);
      }

    } catch (error) {
      console.error('Error fetching driver details:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoadingDetails(false);
    }
  };


  useEffect(() => {
    if (tripDetails) {
      if (tripDetails.shareTripUrl) {
        window.open(tripDetails.shareTripUrl, '_blank', 'noopener,noreferrer');
        dispatch({
          type: "CLEAR_SELECTED_TRIP",
      });
      } else {
        console.error('No URL returned from API');
      }
      
    }
  }, [tripDetails]);

  const maskedPhone = driver?.mobile
    ? driver.mobile.replace(/.(?=.{3})/g, '*')
    : '';

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const renderAvatar = () => {
    if (driver?.avtar?.url) {
      return (
        <div className="avatar">
          <img 
            src={driver.avtar.url} 
            alt={`${driver.firstName} ${driver.lastName}`}
            className="avatar-image"
          />
        </div>
      );
    }
    return (
      <div className="avatar">
        {getInitials(driver.firstName, driver.lastName)}
      </div>
    );
  };

  return (
    <InfoWindow
      position={position}
      onCloseClick={onClose}
      options={{ 
        maxWidth: 280,
        pixelOffset: new window.google.maps.Size(0, -10),
        closeBoxURL: ''
      }}
    >
      <div className="info-window-container">
        <button onClick={onClose} className="close-button">
          <CloseIcon sx={{ fontSize: '18px' }} />
        </button>

        {loading ? (
          <div className="loading-state">
            <p>Loading...</p>
          </div>
        ) : !driver ? (
          <div className="loading-state">
            <p>No driver details available.</p>
          </div>
        ) : (
          <div className="content-wrapper">
            <div className="driver-details">
              {renderAvatar()}
              <div className="details-container">
                <div className="name-row">
                  <h3>{driver.firstName} {driver.lastName}</h3>
                </div>
                
                <div className="vehicle-row">
                  <div className="vehicle-info">
                    <DirectionsCarIcon sx={{ fontSize: '18px', marginRight: '4px', color: 'black' }} />
                    <span className="number">{driver.vehicleRegNumber}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(driver.vehicleRegNumber, 'vehicle');
                    }}
                    className="copy-button"
                    title="Copy vehicle number"
                  >
                    {copiedField === 'vehicle' ? (
                      <CheckIcon sx={{ fontSize: '14px', color: '#4CAF50' }}/>
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: '14px' }}/>
                    )}
                  </button>
                </div>

                <div className="phone-row">
                  <div className="phone-info">
                    <PhoneIcon sx={{ fontSize: '18px', marginRight: '4px', color: 'black'  }} />
                    <span>{maskedPhone}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(driver.mobile, 'mobile');
                    }}
                    className="copy-button"
                    title="Copy mobile number"
                  >
                    {copiedField === 'mobile' ? (
                      <CheckIcon sx={{ fontSize: '14px', color: '#4CAF50' }}/>
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: '14px' }}/>
                    )}
                  </button>
                </div>

                <div className="location-row">
                  <div className="location-info">
                    <span className="info-label">Location: </span>
                    <span 
                      className="info-value location" 
                      title={`${position.lat}, ${position.lng}`}
                    >
                      {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* View Details Link */}
            {updatedSelectedDriver?.status=='onTrip' && <div className="view-details-container">
              <button
                onClick={handleViewDetails}
                disabled={isLoadingDetails}
                className="view-details-link"
                style={{
                  color: '#0F538D',
                  background: 'none',
                  border: 'none',
                  padding: '4px 0',
                  fontSize: '12px',
                  textDecoration: 'underline',
                  cursor: isLoadingDetails ? 'not-allowed' : 'pointer',
                  opacity: isLoadingDetails ? 0.6 : 1,
                  float: 'right',
                  marginTop: '8px'
                }}
              >
                {isLoadingDetails ? 'Loading...' : 'View details'}
              </button>
            </div>}
          </div>
        )}
      </div>
    </InfoWindow>
  );
}